<template>
  <div class="w-full h-full">
    <Card>
      <CardHeader class="flex-row items-start justify-between gap-4 space-y-0">
        <div class="flex-1">
          <CardTitle>Bank connections</CardTitle>
          <CardDescription>View the status of open banking connections</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-4">
          <div class="flex items-center gap-2">
            <Input v-model="connectionIdSearch" placeholder="Search by connection id" @keyup.enter="getConnectionFromId(connectionIdSearch)" />
            <Button size="sm" variant="outline" @click="getConnectionFromId(connectionIdSearch)"><Search class="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" @click="getConnections(0, 50)"><X class="w-4 h-4" /></Button>
          </div>
          <div class="flex items-center gap-2">
            <Input v-model="userIdSearch" placeholder="Search by user id" @keyup.enter="getConnectionsForAnEndUser(0, 25, userIdSearch)" />
            <Button size="sm" variant="outline" @click="getConnectionsForAnEndUser(0, 25, userIdSearch)"><Search class="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" @click="getConnections(0, 50)"><X class="w-4 h-4" /></Button>
          </div>
        </div>
        <DataTable
          :headers="headersTable1"
          :items="bankConnections"
          :loading="loading"
          :error="error"
          @retry="getConnections(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
        >
          <template #item-id="bankConnections">
            <CopyId :value="bankConnections.id" :max-width="120" />
          </template>
          <template #item-endUserId="bankConnections">
            <CopyId :value="bankConnections.endUserId" :max-width="120" />
          </template>
          <template #item-bank="bankConnections">
            <div class="flex items-center gap-2.5">
              <img v-if="bankConnections.accountLogo" :src="bankConnections.accountLogo" class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white" alt="" />
              <span class="text-sm font-medium truncate" :title="bankConnections.accountName">{{ bankConnections.accountName }}</span>
            </div>
          </template>
          <template #item-isactive="bankConnections">
            <Badge v-if="bankConnections.isActive" variant="success">LIVE</Badge>
            <Badge v-else variant="destructive">EXPIRED</Badge>
          </template>
          <template #item-created="bankConnections">
            <span class="text-sm text-muted-foreground whitespace-nowrap">{{ niceDateTime(bankConnections.created) }}</span>
          </template>
          <template #item-lastAccessed="bankConnections">
            <span class="text-sm text-muted-foreground whitespace-nowrap">{{ niceDateTime(bankConnections.lastAccessed) }}</span>
          </template>
          <template #item-disconnect="bankConnections">
            <div class="flex justify-end items-center gap-1">
              <Button
                v-if="bankConnections.isActive"
                size="sm"
                variant="destructive"
                @click="deleteConnection(bankConnections.id)"
              >
                <X class="w-4 h-4" />End connection
              </Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Input,
  CopyId,
} from '@boogi/ui'
import { Search, X } from 'lucide-vue-next'
import { DateTime } from 'luxon'
import { DataTable } from '@boogi/ui'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    Button,
    Badge,
    Input,
    CopyId,
    Search,
    X,
  },
  props: {},

  data() {
    return {
      lastRequest: 'ALL', // ALL / USER / ID

      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,

      userSearch: {
        publisher: '',
        userId: '',
      },
      connectionIdSearch: '',
      userIdSearch: '',

      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: true,
      error: false,
      headersTable1: [
        { text: 'Id', value: 'connectionId' },
        { text: 'Account Id', value: 'accountId' },
        { text: 'User id', value: 'endUserId' },
        { text: 'Bank', value: 'bank' },
        { text: 'Connection status', value: 'isActive' },
        { text: 'Created', value: 'created' },
        { text: 'Last accessed', value: 'lastAccessed' },
        { text: 'Disconnect', value: 'disconnect', align: 'right' },
      ],
      bankConnections: [],
    }
  },
  created() {
    this.getConnections(0, 50)
  },
  methods: {
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },

    async getConnectionsForAnEndUser(page, size, search) {
      this.lastRequest = 'ALL'
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      this.bankConnections = []

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/account-requisition/user/${search}?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.bankConnections = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async getConnectionFromId(search) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      this.bankConnections = []

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/account-requisition/requisition/${search}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.bankConnections = object
          this.serverItemsLength = object.length
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async deleteConnection(search) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true
        this.bankConnections = []

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/account-requisition/requisition/${search}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getConnections(0, 25)
        }
      } catch (error) {
        return
      }
    },

    async getConnections(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.lastRequest = 'ALL'
      this.loading = true
      this.error = false
      this.bankConnections = []

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/account-requisition?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.bankConnections = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {},
  mounted() {
    document.title = 'Dashboard | Bank connections'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        if (this.lastRequest == 'ALL') this.getConnections(val.page - 1, val.rowsPerPage)
        if (this.lastRequest == 'USER')
          this.getConnectionsForAnEndUser(val.page - 1, val.rowsPerPage, this.userSearch)
      },
    },
  },
}
</script>
